import { multiSelectionOptions } from "@/components/controls/MultiSelect";
import { ValidationRule, commonRules } from "@/utils/validation";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { TFunction } from "i18next";

// Currency fields carry their currency code (e.g. "ILS") in config.defaultValue.
// Unknown codes fall back to the code itself rather than a wrong symbol.
export const getConfiguredCurrencySymbol = (
  config: { defaultValue?: string } | null | undefined
): string | undefined => {
  const currencyCode = config?.defaultValue;
  if (!currencyCode) return undefined;
  return getCurrencySymbol(currencyCode) || currencyCode;
};

export const parseOptions = (config: any) => {
  const options = config?.options;

  if (Array.isArray(options)) {
    return options.map((option) => ({
      id: option,
      displayName: option
    }));
  } else if (typeof options === "object" && options !== null) {
    return Object.entries(options).map(([id, name]) => ({
      id,
      displayName: String(name)
    }));
  }
  return [];
};

export const parseMultiSelectOptions = (config: any): multiSelectionOptions => {
  const options = config?.options;

  if (Array.isArray(options)) {
    return options.map((option) => ({
      label: option,
      value: option
    }));
  } else if (typeof options === "object" && options !== null) {
    return Object.entries(options).map(([id, name]) => ({
      label: String(name),
      value: id
    }));
  }
  return [];
};

export const getValidationForDisplayType = (
  displayType: string,
  t: TFunction,
  config?: any
): { customRules: ValidationRule[] } | undefined => {
  switch (displayType) {
    case "email":
      return {
        customRules: [commonRules.email(t("validation.invalidEmail"))]
      };

    case "number":
      const minValue = config?.minValue ? Number(config.minValue) : undefined;
      const maxValue = config?.maxValue ? Number(config.maxValue) : undefined;

      let message = t("validation.invalidNumber");
      if (minValue !== undefined && maxValue !== undefined) {
        message = t("validation.numberRange", { min: minValue, max: maxValue });
      } else if (minValue !== undefined) {
        message = t("validation.numberMin", { min: minValue });
      } else if (maxValue !== undefined) {
        message = t("validation.numberMax", { max: maxValue });
      }

      return {
        customRules: [
          commonRules.number({
            min: minValue,
            max: maxValue,
            message
          })
        ]
      };

    case "percentage":
      return {
        customRules: [
          commonRules.number({
            min: 0,
            max: 100,
            message: t("validation.invalidPercentage")
          })
        ]
      };

    case "currency":
      return {
        customRules: [
          commonRules.number({
            message: t("validation.invalidCurrency")
          })
        ]
      };

    case "url":
      return {
        customRules: [commonRules.url(t("validation.invalidUrl"))]
      };

    default:
      return undefined;
  }
};

export const formatSCOFieldValue = (
  value: any,
  t: (key: string) => string
): string => {
  if (value === null || value === undefined || value === "") {
    return t("unknown");
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "boolean") {
    return value ? t("yes") : t("no");
  }

  return value.toString();
};

/**
 * Context interface for creating new options in select fields
 */
export interface CreateNewOptionContext {
  setNewOptionName?: (name: string) => void;
  setCurrentFieldForNewOption?: (
    field: { fieldId: string; fieldKey: string } | null
  ) => void;
  setShowCreateOptionModal?: (show: boolean) => void;
}

export const createAddNewOptionHandler =
  (
    context: CreateNewOptionContext,
    fieldId: string,
    fieldKey: string,
    searchTerm: string
  ) =>
  (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    context.setNewOptionName?.(searchTerm);
    context.setCurrentFieldForNewOption?.({ fieldId, fieldKey });
    context.setShowCreateOptionModal?.(true);
  };

export const shouldShowCreateNewButton = (searchTerm: string): boolean => {
  return searchTerm.trim().length > 0;
};

export const shouldDisableCreateNewButton = (
  searchTerm: string,
  options: any[]
): boolean => {
  if (!searchTerm.trim()) return false;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  return options.some((option) => {
    const optionName =
      option?.displayName || option?.name || option?.label || "";
    return optionName.toLowerCase() === normalizedSearchTerm;
  });
};

export const getParsedFieldValue = (value: any, fieldType: string) => {
  if (fieldType === "dateRange") {
    if (typeof value === "string" && value.trim() !== "") {
      try {
        const parsed = JSON.parse(value);
        if (
          parsed &&
          typeof parsed === "object" &&
          parsed.fromDate &&
          parsed.toDate
        ) {
          return parsed;
        }
      } catch {
        console.warn(`Failed to parse dateRange JSON value: ${value}`);
      }
    }
    return value;
  }

  return value;
};

export const formatMultiSelectValueForDisplay = (
  value: any,
  fallback: string = ""
): string => {
  if (!value) return fallback;

  if (Array.isArray(value)) {
    const filtered = value.filter(
      (v: any) =>
        v !== "none" &&
        v !== "null" &&
        v !== null &&
        v !== undefined &&
        v !== ""
    );
    return filtered.length > 0 ? filtered.join(", ") : fallback;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (
      trimmed === "" ||
      trimmed === "[]" ||
      trimmed === "['']" ||
      trimmed === '[""]' ||
      trimmed === '["none"]' ||
      trimmed === "['none']"
    ) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const filtered = parsed.filter(
          (v: any) =>
            v !== "none" &&
            v !== "null" &&
            v !== null &&
            v !== undefined &&
            v !== ""
        );
        return filtered.length > 0 ? filtered.join(", ") : fallback;
      }
    } catch {
      const arrayStyleMatch = trimmed.match(/^\[(.+)\]$/);
      if (arrayStyleMatch) {
        const innerContent = arrayStyleMatch[1];
        const items = innerContent
          .split(",")
          .map((item: string) => item.trim().replace(/^['"]|['"]$/g, ""));
        const filtered = items.filter(
          (item: string) =>
            item.length > 0 && item !== "none" && item !== "null"
        );
        return filtered.length > 0 ? filtered.join(", ") : fallback;
      }
    }

    return trimmed || fallback;
  }

  return String(value) || fallback;
};
